import { format, parse } from 'date-fns';
import {
  PRODUCTION_DATA_SOURCE_LABELS,
  isDailyProductionSource,
} from 'components/production-source-dropdown';
import type {
  ProductionDataSource,
  WellVersionsResponse,
} from 'types/api-responses';
import type { FittedProduction } from './atoms';
import type { VersionsBySource, WellVersions } from './types';
import { currentOrFirstVersion } from './utils';

/**
 * The production version a saved DCA forecast records, as an ISO date. A
 * read of a daily source's current version is judged as of the day it ran,
 * so it records that day, and every later read of the forecast leaves out
 * the same short latest month. The server reads "current" as of its UTC day
 * (Date.current in Query::BulkWellDailyProduction#version_date), so this is
 * the UTC day too. Bulk regression records the same day on the server
 * (DcaWorkspace::RegressionService#production_latest_update). Setting a
 * Rails time zone would need all three changed together. Any other version
 * records the day it began.
 *
 * @param {ProductionDataSource} source - The source the production is read from.
 * @param {WellVersionsResponse} version - The version read.
 * @param {Date} readOn - When the production was read.
 * @return {string} The version id to save, such as "2026-10-23".
 */
export const savedProductionVersionId = (
  source: ProductionDataSource,
  version: WellVersionsResponse,
  readOn: Date
): string => {
  if (isDailyProductionSource(source) && version.versionId === 'current') {
    return readOn.toISOString().slice(0, 10);
  }

  const beginDay = version.beginTime.split(' ')[0];
  return format(parse(beginDay, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd');
};

/**
 * The production a DCA forecast is saved with. A fitted forecast keeps the
 * production its fit read, even if the source or version changed after the
 * fit. One edited by hand, with no fit, is saved with the production
 * selected now.
 *
 * @param {FittedProduction | null} fitted - What the last fit read, or null without one.
 * @param {ProductionDataSource} selectedSource - The source selected now.
 * @param {WellVersionsResponse | null} selectedVersion - The version selected now.
 * @param {Date} now - When Save runs.
 * @return {{ productionSource: ProductionDataSource, productionVolumeVersionId: string } | null}
 *   The source and version id to save, or null when there is no version yet.
 */
export const savedProduction = (
  fitted: FittedProduction | null,
  selectedSource: ProductionDataSource,
  selectedVersion: WellVersionsResponse | null,
  now: Date
): {
  productionSource: ProductionDataSource;
  productionVolumeVersionId: string;
} | null => {
  if (fitted) {
    return fitted.productionVolumeVersionId
      ? {
          productionSource: fitted.source,
          productionVolumeVersionId: fitted.productionVolumeVersionId,
        }
      : null;
  }
  if (!selectedVersion) return null;

  return {
    productionSource: selectedSource,
    productionVolumeVersionId: savedProductionVersionId(
      selectedSource,
      selectedVersion,
      now
    ),
  };
};

/**
 * A version's id in the combined list: its source, then its own id. Sources
 * share version ids such as 'current', so the source keeps them apart.
 *
 * @param {ProductionDataSource} source - The version's source.
 * @param {string} versionId - The version's own id.
 * @return {string} The id in the combined list.
 */
const combinedVersionId = (source: ProductionDataSource, versionId: string) =>
  `${source}:${versionId}`;

/**
 * The versions of every shown source as one list, in the order the sources
 * were picked. Each is labelled with its source's name, such as
 * "IHS - Version 28", and given an id unique across the sources.
 *
 * @param {ProductionDataSource[]} sources - The shown sources, in pick order.
 * @param {VersionsBySource} versionsBySource - Each source's versions.
 * @return {WellVersions} The combined list.
 */
export const combineSourceVersions = (
  sources: ProductionDataSource[],
  versionsBySource: VersionsBySource
): WellVersions =>
  sources.flatMap((source) =>
    (versionsBySource[source] ?? []).map((version) => ({
      ...version,
      versionId: combinedVersionId(source, version.versionId),
      label: `${PRODUCTION_DATA_SOURCE_LABELS[source]} - ${version.label}`,
    }))
  );

/**
 * Splits a selection from the combined list back into each source's own
 * versions, with their own ids, so production is asked for as before. Every
 * shown source with versions listed gets an entry, empty when none of its
 * versions is selected. A source with no versions listed yet gets none, so it
 * still starts on its current version once its versions arrive.
 *
 * @param {WellVersions} selected - Versions picked from the combined list.
 * @param {ProductionDataSource[]} sources - The shown sources.
 * @param {VersionsBySource} versionsBySource - Each source's versions.
 * @return {VersionsBySource} Each source's selected versions.
 */
export const splitSourceVersions = (
  selected: WellVersions,
  sources: ProductionDataSource[],
  versionsBySource: VersionsBySource
): VersionsBySource => {
  const selectedIds = new Set(selected.map((version) => version.versionId));
  return Object.fromEntries(
    sources
      .filter((source) => (versionsBySource[source] ?? []).length > 0)
      .map((source) => [
        source,
        (versionsBySource[source] ?? []).filter((version) =>
          selectedIds.has(combinedVersionId(source, version.versionId))
        ),
      ])
  );
};

/**
 * Each shown source's selected versions: the ones picked for it, or its
 * current version when nothing was picked for it.
 *
 * @param {ProductionDataSource[]} sources - The shown sources.
 * @param {VersionsBySource} picked - The versions picked for each source.
 * @param {VersionsBySource} versionsBySource - Each source's versions.
 * @return {VersionsBySource} Each shown source's selected versions.
 */
export const withCurrentVersions = (
  sources: ProductionDataSource[],
  picked: VersionsBySource,
  versionsBySource: VersionsBySource
): VersionsBySource =>
  Object.fromEntries(
    sources.map((source) => {
      const pickedVersions = picked[source];
      if (pickedVersions) return [source, pickedVersions];

      const current = currentOrFirstVersion(versionsBySource[source] ?? []);
      return [source, current ? [current] : []];
    })
  );
